import { type ComponentType as ReactComponent, createContext, type ReactNode } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { type PaddingType } from "../PaddingInput";
import { type BorderSettingsType } from "./BorderSettingsDialog";
import { type DragDropManager } from "./DragDropManager";
import { type TextReplacement } from "./InternalComponents/Text/TextReplacement";

export interface DragDropEditorContextType {
    manager: DragDropManager;
    items: Record<string, DragDropEditItemDefinition>;
    accept: Array<string>;
    selected: string | null;
    highlight: string | null;
    setSelected: (selected: string | null) => void;
    onCreate: (type: string) => ComponentType;
    onConfigure: () => void;
    textReplacement: TextReplacement;
}

export type ComponentOptions<TData = any> = Omit<ComponentType<TData>, "type">;

export interface DragDropSettingsProps extends ComponentOptions {
    id: string;
}

export interface ComponentCreateOptions {
    createContainer: (components?: Array<string>) => string;
}

export interface DragDropWrapperProps {
    preview: boolean;
    data: Record<string, unknown>;
    children: ReactNode;
}

export interface DragDropItemDefinition {
    component: ReactComponent<ComponentOptions>;
}

export type DragDropItemMap = Record<string, DragDropItemDefinition>;

export interface DragDropEditItemDefinition<
    TComponent extends ComponentOptions = ComponentOptions
> extends DragDropItemDefinition {
    edit?: ReactComponent<TComponent>;
    settings: ReactComponent<DragDropSettingsProps>;
    onCreate: (options: ComponentCreateOptions) => TComponent;
    name: string;
    icon: IconProp;
    styles: Array<keyof ComponentStyles>;
    getContainers?: (component: TComponent) => Array<string>;
}

export type DragDropEditItemMap = Record<string, DragDropEditItemDefinition>;

export const DragDropEditorContext = createContext<DragDropEditorContextType>(undefined!);

export interface ComponentStyles {
    color?: string;
    background?: string;
    borderColor?: string;
    promotionColor?: string;
    fontSize?: string;
    fontWeight?: "normal" | "bold" | "bolder";
    width?: string;
    height?: string;
    textAlign?: "left" | "center" | "right";
    padding?: PaddingType;
    image?: string | null;
    horizAlign?: "left" | "center" | "right";
    border?: BorderSettingsType;
}

export interface DragDropMoveItem {
    type: string;
    index: number;
    container: string;
}

export interface DragDropNewItem {
    type: string;
    new: boolean;
}

export function isDragDropMoveItem(item: DragDropItem): item is DragDropMoveItem {
    return !isDragDropNew(item);
}

export function isDragDropNew(item: DragDropItem): item is DragDropNewItem {
    return "new" in item;
}

export type DragDropItem = DragDropMoveItem | DragDropNewItem;

export interface DragDropTableColumnType {
    name: string;
    column: string;
    enabled: boolean;
}

export interface ComponentType<TValue = unknown> {
    type: string;
    value: TValue;
    style: ComponentStyles;
}

export interface ColumnType {
    components: Array<ComponentType>;
    width: number;
}

export interface ColumnComponentType extends ComponentType<[ ColumnType, ColumnType ]> {
    type: "column";
}

export interface InternalColumnType {
    id: string;
    width: number;
}

export function isInternalColumnType(data: unknown): data is InternalColumnType {
    if(typeof data !== "object") {
        return false;
    }

    if(data === null) {
        return false;
    }

    return "id" in data && typeof data.id === "string" &&
        "width" in data && typeof data.width === "number";
}

export interface InternalColumnComponentType extends ComponentType {
    type: "internal_column";
    value: [ InternalColumnType, InternalColumnType ];
}

export function isColumnComponentType(component: ComponentType): component is ColumnComponentType {
    if(component.type === "column") {
        return Array.isArray(component.value);
    }

    return false;
}

export function isInternalColumnComponentType(component: ComponentType): component is InternalColumnComponentType {
    if(component.type === "internal_column") {
        return Array.isArray(component.value);
    }

    return false;
}

export const DRAG_DROP_ROOT_CONTAINER_ID = "_root";

export type DragDropImageLoadCallback = (src: string) => void;
export const DragDropImageLoadContext = createContext<
    undefined | DragDropImageLoadCallback
>(undefined);
